import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import * as Sentry from "@sentry/angular";
import { catchError, map } from "rxjs/operators";
import { MessageService } from "common";
import { adaptConversionMetrics, ConversionMetricsResponse } from "./api-models/conversion-metrics-api.model";
import { adaptExecutiveSnapshot, ExecutiveSnapshotResponse } from "./api-models/executive-snapshot-api.model";
import { adaptPartnerScorecard, PartnerScorecardResponse } from "./api-models/partner-scorecard-api.model";
import { adaptPipelineHealth, PipelineHealthResponse } from "./api-models/pipeline-health-api.model";
import { adaptSmartInsights, SmartInsightsResponse } from "./api-models/smart-insights-api.model";
import { adaptTrendMetrics, TrendMetricsResponse } from "./api-models/trend-metrics-api.model";
import { AppSettings } from "../../../../app.settings";
import { ApiPaths } from "../../../core/const/api-paths";
import { ApiUrls } from "../../../shared/SharedConstants";
import { ConversionMetrics, ConversionMetricsRequest } from "../domain/model/conversion-metrics.model";
import { DashboardPeriodRequest } from "../domain/model/dashboard-filters.model";
import { DashboardWidgetsData } from "../domain/model/dashboard.model";
import { ExecutiveSnapshot, ExecutiveSnapshotRequest } from "../domain/model/executive-snapshot.model";
import { PartnerScorecard, PartnerScorecardRequest } from "../domain/model/partner-scorecard.model";
import { PipelineHealth } from "../domain/model/pipeline-health.model";
import { SmartInsights, SmartInsightsRequest } from "../domain/model/smart-insights.model";
import { TrendMetrics, TrendMetricsRequest } from "../domain/model/trend-metrics.model";

@Injectable({
  providedIn: "root",
})
export class DashboardRemote {
  constructor(
    private http: HttpClient,
    private settings: AppSettings,
    private messageService: MessageService
  ) { }

  getDashboardWidgets(): Observable<DashboardWidgetsData> {
    const errorMessage = "Something went wrong while retrieving dashboard widget data, please try again later";

    return this.http.get<DashboardWidgetsData>(ApiUrls(this.settings).DashboardWidgets).pipe(catchError(() => {
      this.messageService.error(errorMessage);
      Sentry.captureException(errorMessage);
      return of(null);
    }));
  }

  /**
   * Rethrows untouched, without a toast: the snapshot's own error card is its error surface, so a toast
   * would say the same thing twice (and repeat the validator's 400 message verbatim).
   */
  getExecutiveSnapshot(request: ExecutiveSnapshotRequest): Observable<ExecutiveSnapshot> {
    return this.http
      .get<ExecutiveSnapshotResponse>(ApiPaths.dashboard.executiveSnapshot, {
        params: { from: request.from, to: request.to, scope: request.scope },
      })
      .pipe(map(adaptExecutiveSnapshot));
  }

  /** Rethrows untouched, without a toast: the pipeline page's own error card is its error surface. */
  getPipelineHealth(request: DashboardPeriodRequest): Observable<PipelineHealth> {
    return this.http
      .get<PipelineHealthResponse>(ApiPaths.dashboard.pipelineHealth, {
        params: { from: request.from, to: request.to, scope: request.scope },
      })
      .pipe(map(adaptPipelineHealth));
  }

  /** Rethrows untouched, without a toast: the conversion page's own error card is its error surface. */
  getConversionMetrics(request: ConversionMetricsRequest): Observable<ConversionMetrics> {
    return this.http
      .get<ConversionMetricsResponse>(ApiPaths.dashboard.conversionMetrics, {
        params: { from: request.from, to: request.to, scope: request.scope, period: request.period },
      })
      .pipe(map(adaptConversionMetrics));
  }

  /** Rethrows untouched, without a toast: the trend page's own error card is its error surface. */
  getTrendMetrics(request: TrendMetricsRequest): Observable<TrendMetrics> {
    return this.http
      .get<TrendMetricsResponse>(ApiPaths.dashboard.trendMetrics, {
        params: { from: request.from, to: request.to, scope: request.scope, period: request.period },
      })
      .pipe(map(adaptTrendMetrics));
  }

  /** Rethrows untouched, without a toast: the insights page's own error card is its error surface. */
  getSmartInsights(request: SmartInsightsRequest): Observable<SmartInsights> {
    return this.http
      .get<SmartInsightsResponse>(ApiPaths.dashboard.smartInsights, {
        params: { from: request.from, to: request.to, scope: request.scope, period: request.period },
      })
      .pipe(map(adaptSmartInsights));
  }

  /** Scope only: the server derives the window. Rethrows untouched, without a toast, like its siblings. */
  getPartnerScorecard(request: PartnerScorecardRequest): Observable<PartnerScorecard> {
    return this.http
      .get<PartnerScorecardResponse>(ApiPaths.dashboard.partnerScorecard, { params: { scope: request.scope } })
      .pipe(map(adaptPartnerScorecard));
  }
}
